/**
 * Multi-Canvas Diagram Editor Wrapper
 * Adds tab-based multi-canvas functionality to DiagramEditorEnhanced
 * Manages multiple diagram canvases with state persistence
 */

import { useCallback, useEffect, CSSProperties } from 'react'
import DiagramEditorEnhanced from './DiagramEditorEnhanced'
import { useCollabPages } from './collab/useCollabPages'
import CanvasTabBar from './components/CanvasTabBar'
import { useMultiCanvas } from './hooks/useMultiCanvas'
import { useModal } from './components/modals/ModalProvider'
import type { DiagramData } from './types/schema'
import './styles/multi-canvas-editor.css'

export interface DiagramEditorMultiCanvasProps {
  initialData?: DiagramData
  enablePersistence?: boolean
  onChange?: (data: DiagramData) => void
  onExport?: (json: string) => void
  onCanvasAdd?: (addTabFn: (name?: string, data?: DiagramData) => string) => void
  readonly?: boolean
  showToolbar?: boolean
  showMinimap?: boolean
  showControls?: boolean
  showPropertiesPanel?: boolean
  showNodePalette?: boolean
  onTogglePropertiesPanel?: () => void
  onToggleNodePalette?: () => void
  autoLayout?: boolean
  theme?: 'light' | 'dark'
  className?: string
  style?: CSSProperties
  showGrid?: boolean
  /** Opt-in real-time collaboration room, threaded to DiagramEditorEnhanced. */
  collab?: { url: string; room: string }
  /** localStorage key for the canvas tab set. Set per-project so each project
   *  persists its own canvases independently. Defaults to a shared key. */
  persistenceKey?: string
}

export default function DiagramEditorMultiCanvas({
  initialData,
  enablePersistence = true,
  onChange,
  onExport,
  onCanvasAdd,
  collab,
  persistenceKey,
  ...editorProps
}: DiagramEditorMultiCanvasProps) {
  const modal = useModal()

  // Multi-canvas state management
  const {
    tabs,
    activeTabId,
    addTab,
    upsertRoomPage,
    removeRoomPages,
    removeTab,
    duplicateTab,
    switchTab,
    renameTab,
    updateTabData,
    updateTabViewport,
    updateTabSelection,
    reorderTabs,
    closeOthers,
    closeToRight,
    getActiveTab,
    // In a collab room the ROOM is the single source of truth: don't seed the
    // host's `initialData` as the first tab, and don't restore/persist the shared
    // localStorage tab set (it bleeds stale pages from other rooms/sessions into
    // this room and overwrites live pages before sync). Both are major data-loss
    // sources for multi-page rooms.
  } = useMultiCanvas(collab ? undefined : initialData, enablePersistence && !collab, persistenceKey)

  // RT-8: multi-page room sync. When a collab room is provided, the tab set is
  // mirrored to the room's `pages` map (MCP/peers can add/edit pages → tabs).
  const { flushTab } = useCollabPages(collab, { tabs, upsertRoomPage, removeRoomPages })

  // Flush locally-changed tabs back to the room (hash-guarded against echo).
  useEffect(() => {
    if (!collab) return
    for (const t of tabs) flushTab(t)
  }, [collab, tabs, flushTab])

  // Get current active tab
  const activeTab = getActiveTab()

  // Expose addTab function to parent
  useEffect(() => {
    if (onCanvasAdd) {
      onCanvasAdd(addTab)
    }
  }, [onCanvasAdd, addTab])

  // Handle external data updates (e.g., when loading from JSON). NEVER do this in
  // a collab room — it would overwrite the active room page (e.g. the open
  // "Sequence" tab) with the host's default diagram, then flush that into the room.
  useEffect(() => {
    if (!collab && initialData && activeTabId) {
      updateTabData(activeTabId, initialData)
    }
  }, [initialData])

  // Handle diagram changes - update active tab data
  const handleDiagramChange = useCallback((newData: DiagramData) => {
    updateTabData(activeTabId, newData)
    onChange?.(newData)
  }, [activeTabId, updateTabData, onChange])

  // Handle export
  const handleExport = useCallback((json: string) => {
    onExport?.(json)
  }, [onExport])

  // Handle tab add
  const handleAddTab = useCallback(() => {
    addTab()
  }, [addTab])

  // Handle tab close
  const handleTabClose = useCallback((tabId: string) => {
    removeTab(tabId)
  }, [removeTab])

  // Handle tab click
  const handleTabClick = useCallback((tabId: string) => {
    switchTab(tabId)
  }, [switchTab])

  // Handle tab rename
  const handleTabRename = useCallback((tabId: string, name: string) => {
    renameTab(tabId, name)
  }, [renameTab])

  // Handle tab reorder
  const handleTabReorder = useCallback((startIndex: number, endIndex: number) => {
    reorderTabs(startIndex, endIndex)
  }, [reorderTabs])

  // Handle tab duplicate
  const handleTabDuplicate = useCallback((tabId: string) => {
    duplicateTab(tabId)
  }, [duplicateTab])

  // Handle close others
  const handleCloseOthers = useCallback((tabId: string) => {
    closeOthers(tabId)
  }, [closeOthers])

  // Handle close to right
  const handleCloseToRight = useCallback((tabId: string) => {
    closeToRight(tabId)
  }, [closeToRight])

  // Keyboard shortcuts for tab navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+T or Cmd+T - New tab
      if ((e.ctrlKey || e.metaKey) && e.key === 't' && !e.shiftKey) {
        e.preventDefault()
        addTab()
      }

      // Ctrl+W or Cmd+W - Close tab (with confirmation if unsaved)
      if ((e.ctrlKey || e.metaKey) && e.key === 'w') {
        e.preventDefault()
        const currentTab = getActiveTab()
        if (currentTab && tabs.length > 1) {
          if (currentTab.unsavedChanges) {
            modal.confirm({
              message: `Close "${currentTab.name}"? You have unsaved changes.`,
              type: 'warning'
            }).then((confirmed) => {
              if (confirmed) {
                removeTab(currentTab.id)
              }
            })
          } else {
            removeTab(currentTab.id)
          }
        }
      }

      // Ctrl+Tab - Next tab
      if ((e.ctrlKey || e.metaKey) && e.key === 'Tab' && !e.shiftKey) {
        e.preventDefault()
        const currentIndex = tabs.findIndex(t => t.id === activeTabId)
        const nextIndex = (currentIndex + 1) % tabs.length
        switchTab(tabs[nextIndex].id)
      }

      // Ctrl+Shift+Tab - Previous tab
      if ((e.ctrlKey || e.metaKey) && e.key === 'Tab' && e.shiftKey) {
        e.preventDefault()
        const currentIndex = tabs.findIndex(t => t.id === activeTabId)
        const prevIndex = (currentIndex - 1 + tabs.length) % tabs.length
        switchTab(tabs[prevIndex].id)
      }

      // Ctrl+Shift+PgUp - Move tab left
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'PageUp') {
        e.preventDefault()
        const currentIndex = tabs.findIndex(t => t.id === activeTabId)
        if (currentIndex > 0) {
          reorderTabs(currentIndex, currentIndex - 1)
        }
      }

      // Ctrl+Shift+PgDn - Move tab right
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'PageDown') {
        e.preventDefault()
        const currentIndex = tabs.findIndex(t => t.id === activeTabId)
        if (currentIndex < tabs.length - 1) {
          reorderTabs(currentIndex, currentIndex + 1)
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [addTab, removeTab, switchTab, reorderTabs, getActiveTab, tabs, activeTabId])

  if (!activeTab) {
    return (
      <div className="multi-canvas-editor">
        <div className="multi-canvas-empty">
          <p>No active canvas</p>
          <button onClick={handleAddTab}>Create Canvas</button>
        </div>
      </div>
    )
  }

  return (
    <div className="multi-canvas-editor">
      {/* Main Editor */}
      <div className="multi-canvas-content">
        {/*
          Key the editor by activeTabId so it remounts on tab switch and picks
          up the new tab's defaultViewport. This is the simplest correct approach
          for restoring per-canvas camera/selection (vs. imperative setViewport).
        */}
        <DiagramEditorEnhanced
          key={activeTabId}
          data={activeTab.data}
          onChange={handleDiagramChange}
          onExport={handleExport}
          defaultViewport={activeTab.viewport}
          onViewportChange={(vp) => updateTabViewport(activeTabId, vp)}
          onSelectionChangeIds={(ids) => updateTabSelection(activeTabId, ids)}
          {...editorProps}
        />
      </div>

      {/* Canvas Tab Bar */}
      <CanvasTabBar
        tabs={tabs}
        activeTabId={activeTabId}
        onTabClick={handleTabClick}
        onTabClose={handleTabClose}
        onTabAdd={handleAddTab}
        onTabRename={handleTabRename}
        onTabReorder={handleTabReorder}
        onTabDuplicate={handleTabDuplicate}
        onCloseOthers={handleCloseOthers}
        onCloseToRight={handleCloseToRight}
      />
    </div>
  )
}
